import {
    IFlowUpdatedResponse,
    IScheduleAvailabilitiesResponse,
    IScheduleSessionConfigsResponse,
    ISessionConfig
} from '~/types/server.types';
import { BaseHttpAdapter } from '~/services/adapters/base-http.adapter';
import { TFullTimezone } from '~/utils/timezone.utils';
import { IApiService } from '~/services/interfaces/IApiService';
import { SchedulerEngineAdapter } from '~/services/adapters/scheduler-engine.adapter';
import { v2UpdateSessionConfigPath } from '~/utils/ts-routes.utils';
import { PartialWithRequired } from '~/types/app.types';

export class ScheduleAdapter extends BaseHttpAdapter {
    private schedulerEngineAdapter: SchedulerEngineAdapter;
    constructor(
        apiService: IApiService,
        schedulerEngineAdapter: SchedulerEngineAdapter
    ) {
        super(apiService);
        this.schedulerEngineAdapter = schedulerEngineAdapter;
    }

    async getScheduleSessionConfigs({
        userId,
        forUserId,
        params
    }: {
        userId: string;
        forUserId?: string;
        params?: {
            ignore_day_availabilities?: boolean;
            ignore_time_frame?: boolean;
            get_related_workflows?: boolean;
            from_date?: string;
        };
    }) {
        const _params = {
            ignore_day_availabilities: true,
            ...(forUserId ? { for_user_id: forUserId } : {}),
            ...(params || {})
        };

        return this.apiService.apiRequest<IScheduleSessionConfigsResponse>({
            method: 'get',
            url: `/api/v2/users/${userId}/session_configs`,
            params: _params,
            networkLabel: 'getScheduleSessionConfigs'
        });
    }

    async getCompanyScheduleSessionConfigs({
        companyId
    }: {
        companyId: string;
    }) {
        return this.apiService.apiRequest<IScheduleSessionConfigsResponse>({
            method: 'get',
            url: `/api/v2/companies/${companyId}/session_configs`
        });
    }

    async deleteSessionConfig({ id, userId }: { id: string; userId: string }) {
        return this.apiService.apiRequest<null>({
            method: 'delete',
            url: `/api/v2/users/${userId}/session_configs/${id}`,
            networkLabel: `deleteSessionConfig`
        });
    }

    async cancelSession({
        flowId,
        variableId
    }: {
        flowId: string;
        variableId: string;
    }) {
        return this.apiService.apiRequest<IFlowUpdatedResponse>({
            method: 'put',
            url: `/api/v2/flow/${flowId}/scheduling/${variableId}/cancel`
        });
    }

    async getAvailabilities(params: {
        debugMode?: boolean;
        startDate: string;
        endDate: string;
        timezone: TFullTimezone;
        sessionTypeId: string;
        placementId: string;
        workspaceId: string;
        source: string;
    }) {
        return this.schedulerEngineAdapter.apiRequest<IScheduleAvailabilitiesResponse>(
            {
                method: 'get',
                url: '/api/availability',
                params
            }
        );
    }

    async createScheduleSessionConfig({
        data,
        userId
    }: {
        data: ISessionConfig;
        userId: string;
    }) {
        return this.apiService.apiRequest<ISessionConfig>({
            method: 'post',
            url: `/api/v2/users/${userId}/session_configs`,
            data
        });
    }

    async duplicateSessionConfig({ sessionId }: { sessionId: string }) {
        return this.apiService.apiRequest<ISessionConfig>({
            method: 'post',
            url: `/api/v2/session_configs/copy/${sessionId}`,
            data: { session_id: sessionId },
            networkLabel: `duplicateSessionConfig__${sessionId}`
        });
    }

    async updateSession({
        userId,
        data
    }: {
        userId: string;
        data: PartialWithRequired<ISessionConfig, '_id'>;
    }) {
        return this.apiService.apiRequest<ISessionConfig>({
            method: 'put',
            url: v2UpdateSessionConfigPath(userId, data._id),
            data
        });
    }
}
